import React from 'react'
import { Row, Col, Button } from 'reactstrap'
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid'
import Autocomplete from '@mui/material/Autocomplete'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import MuiButton from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { Chip, MenuItem, TextField } from '@mui/material'
import { withFirebase } from '../../components/Fb'
import DeleteButton from '../../components/Buttons/DeleteButton'
import {
  CONTRACT_STATUSES,
  buildContractFromTemplate,
  labelFor,
  templatesFor,
} from '../../components/utils/contracts'

function ExportToolbar() {
  // ';' + utf8WithBom is the house convention - anything else opens as one column in a
  // Finnish Excel.
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport csvOptions={{ fileName: 'sopimukset', delimiter: ';', utf8WithBom: true }} />
    </GridToolbarContainer>
  )
}

/**
 * The contracts list.
 *
 * Rewritten off the legacy rjsf FalconModal + ReactTable pair. Contracts are no longer
 * hand-authored here: a contract is made from a template, either against a recurring
 * booking (in ReservationModal) or standalone against an organisation (here). The
 * lifecycle beyond 'created'/'cancelled' belongs to the backend, so this view reads it
 * rather than offering it as an editable dropdown.
 */
class ContractsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      contracts: [],
      templates: [],
      organisations: [],
      statusFilter: 'all',
      createOpen: false,
      selectedTemplateId: '',
      selectedOrganisation: null,
      deleteTarget: null,
      working: false,
      error: null,
    }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = async () => {
    try {
      const [snapshot, templates] = await Promise.all([
        this.props.firebase.getContracts(),
        this.props.firebase.getContractTemplates(),
      ])
      const contracts = snapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        // state is the soft-delete marker; status is the signing lifecycle.
        .filter((c) => c.state !== 'deleted')
        .sort((a, b) => String(b.validuntil || '').localeCompare(String(a.validuntil || '')))
      this.setState({ contracts, templates, loading: false })
    } catch (error) {
      console.error('Contracts failed to load', error)
      this.setState({ contracts: [], templates: [], loading: false })
    }
  }

  loadOrganisations = async () => {
    if (this.state.organisations.length > 0) return
    try {
      const snapshot = await this.props.firebase.listenOrganisations().get()
      const organisations = snapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((o) => o.state !== 'deleted')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'fi'))
      this.setState({ organisations })
    } catch (error) {
      console.error('Organisations failed to load', error)
    }
  }

  openCreate = () => {
    this.setState({ createOpen: true, selectedTemplateId: '', selectedOrganisation: null, error: null })
    this.loadOrganisations()
  }

  createContract = async () => {
    const { selectedTemplateId, selectedOrganisation, templates } = this.state
    const template = templates.find((t) => t.id === selectedTemplateId)
    if (!template || !selectedOrganisation) return
    this.setState({ working: true, error: null })
    const contract = buildContractFromTemplate(template, { organisation: selectedOrganisation })
    const result = await this.props.firebase.createContract(contract)
    if (!result || result.error) {
      this.setState({ working: false, error: 'Sopimuksen luonti epäonnistui.' })
      return
    }
    this.setState({ working: false, createOpen: false, loading: true }, this.updateData)
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    if (!target) return
    this.setState({ working: true })
    await this.props.firebase.deleteContract(target.id)
    this.setState({ working: false, deleteTarget: null, loading: true }, this.updateData)
  }

  getFiltered = () => {
    const { contracts, statusFilter } = this.state
    if (statusFilter === 'all') return contracts
    return contracts.filter((c) => (c.status || 'created') === statusFilter)
  }

  getColDefinitions = () => [
    { field: 'user', headerName: 'Asiakas', flex: 2, minWidth: 160 },
    {
      field: 'templatename',
      headerName: 'Sopimusmalli',
      flex: 2,
      minWidth: 150,
      // Contracts written before templates existed only have the legacy type string.
      valueGetter: (value, row) => row.templatename || row.displayName || row.type || '',
    },
    { field: 'contracttype', headerName: 'Tyyppi', flex: 1, minWidth: 110 },
    {
      field: 'signingtype',
      headerName: 'Allekirjoitus',
      width: 130,
      valueGetter: (value, row) => (row.signingtype === 'manual' ? 'Manuaalinen' : 'Sähköinen'),
    },
    {
      field: 'status',
      headerName: 'Tila',
      width: 180,
      renderCell: (params) => {
        const value = params.row.status || 'created'
        return (
          <Chip
            size='small'
            label={labelFor(CONTRACT_STATUSES, value)}
            color={value === 'signed' ? 'primary' : value === 'cancelled' ? 'default' : 'warning'}
            variant={value === 'signed' ? 'filled' : 'outlined'}
          />
        )
      },
    },
    {
      field: 'validuntil',
      headerName: 'Voimassa asti',
      width: 130,
      valueGetter: (value, row) => (row.validuntil ? row.validuntil : 'Toistaiseksi'),
    },
    {
      field: 'file',
      headerName: 'Tiedosto',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.file ? (
          <a href={params.row.file} target='_blank' rel='noopener noreferrer'>
            Lataa
          </a>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        ),
    },
    {
      field: 'delete',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => <DeleteButton disablediv onClick={() => this.setState({ deleteTarget: params.row })} />,
    },
  ]

  renderStatusFilters = () => {
    const options = [{ label: 'Kaikki', value: 'all' }, ...CONTRACT_STATUSES]
    return options.map((option) => (
      <Chip
        key={option.value}
        label={option.label}
        clickable
        size='small'
        color='primary'
        variant={this.state.statusFilter === option.value ? 'filled' : 'outlined'}
        onClick={() => this.setState({ statusFilter: option.value })}
        style={{ marginRight: 4, marginBottom: 4 }}
      />
    ))
  }

  renderCreateDialog = () => {
    const { templates, organisations, selectedTemplateId, selectedOrganisation, working, error } = this.state
    const available = templatesFor(templates, 'organisation', null)

    return (
      <Dialog open={this.state.createOpen} onClose={() => this.setState({ createOpen: false })} maxWidth='sm' fullWidth>
        <DialogTitle>Uusi sopimus organisaatiolle</DialogTitle>
        <DialogContent>
          {available.length === 0 ? (
            <Alert severity='info' sx={{ mt: 1 }}>
              Ei organisaatioille tarkoitettuja sopimusmalleja. Luo malli Sopimusmallit-näkymässä ja aseta
              sen käyttökohteeksi Organisaatio tai Molemmat.
            </Alert>
          ) : (
            <>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Varaukseen liittyvä sopimus tehdään varausikkunassa. Tämä on vuoroista riippumaton sopimus.
              </Typography>
              <TextField
                fullWidth
                select
                size='small'
                label='Sopimusmalli'
                value={selectedTemplateId}
                onChange={(e) => this.setState({ selectedTemplateId: e.target.value })}
                sx={{ mb: 2 }}
              >
                {available.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                size='small'
                options={organisations}
                value={selectedOrganisation}
                getOptionLabel={(option) => option.name || option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => this.setState({ selectedOrganisation: value })}
                renderInput={(params) => <TextField {...params} label='Organisaatio' />}
              />
              {error && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => this.setState({ createOpen: false })} disabled={working}>
            Peruuta
          </MuiButton>
          <MuiButton
            variant='contained'
            color='primary'
            onClick={this.createContract}
            disabled={working || !selectedTemplateId || !selectedOrganisation}
          >
            {working ? 'Luodaan...' : 'Luo sopimus'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    )
  }

  renderDeleteDialog = () => (
    <Dialog open={!!this.state.deleteTarget} onClose={() => this.setState({ deleteTarget: null })} maxWidth='sm' fullWidth>
      <DialogTitle>Poistetaanko sopimus?</DialogTitle>
      <DialogContent>
        <Typography variant='body1' gutterBottom>
          <b>{this.state.deleteTarget ? this.state.deleteTarget.user : ''}</b>
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Sopimus poistuu listalta. Jos tarkoitus on päättää voimassa oleva sopimus, peru se sen sijaan —
          varauksen peruminen merkitsee sopimuksen perutuksi.
        </Typography>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={() => this.setState({ deleteTarget: null })} disabled={this.state.working}>
          Peruuta
        </MuiButton>
        <MuiButton variant='contained' color='error' onClick={this.confirmDelete} disabled={this.state.working}>
          {this.state.working ? 'Poistetaan...' : 'Poista'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  )

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button size='sm' onClick={this.openCreate}>
              Lisää sopimus organisaatiolle
            </Button>
          </Col>
        </Row>
        {this.state.templates.length === 0 && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Ei sopimusmalleja. Sopimuksia ei voi tehdä ennen kuin vähintään yksi malli on luotu
            Sopimusmallit-näkymässä.
          </Alert>
        )}
        <Row>
          <Col className='mb-2'>{this.renderStatusFilters()}</Col>
        </Row>
        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 260px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={this.getFiltered()}
                  columns={this.getColDefinitions()}
                  density='compact'
                  showToolbar
                  slots={{ toolbar: ExportToolbar }}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </div>
            </div>
          </Col>
        </Row>
        {this.renderCreateDialog()}
        {this.renderDeleteDialog()}
      </div>
    )
  }
}

export default withFirebase(ContractsContainer)
